<div class="iching-reading">
  <!-- C1: de situatie -->
  <section class="iching-reading__step iching-reading__situation" data-step="trigrams">
    @if (step('trigrams'); as s) {
      <h4 class="iching-reading__step-title">{{ s.title }}</h4>
      <hd-iching-step-help [step]="s" />
    }
    <div class="iching-reading__situation-body">
      <div class="iching-reading__hexagram">
        <hd-iching-hexagram-figure [lines]="reading().lines" size="large" colorTrigrams />
        <span class="iching-reading__number">#{{ reading().primary }}</span>
        @if (nameRevealed()) {
          @if (hexagram(); as h) {
            <div class="iching-reading__name">
              <span class="iching-reading__chinese">{{ h.chineseName }}</span>
              <span class="iching-reading__title">{{ h.title }}</span>
            </div>
          }
        } @else {
          <button type="button" class="iching-reading__btn" data-test-id="iching-reveal-name" (click)="nameRevealedChange.emit(true)">
            {{ t('humanDesign.iching.reading.revealName') }}
          </button>
        }
      </div>
      <!-- Onder eerst in de DOM (leesvolgorde: jij, dan de omgeving); visueel staat boven bovenaan. -->
      <div class="iching-reading__trigrams">
        @if (lowerTrigram(); as lower) {
          <hd-iching-trigram-card [trigram]="lower" [role]="'lower'" [extreme]="hasMovingIn('lower')" />
        }
        @if (upperTrigram(); as upper) {
          <hd-iching-trigram-card [trigram]="upper" [role]="'upper'" [extreme]="hasMovingIn('upper')" />
        }
      </div>
    </div>

    <button
      type="button"
      class="iching-reading__switch"
      data-test-id="iching-relation-mode"
      role="switch"
      [attr.aria-checked]="relationMode()"
      [class.is-on]="relationMode()"
      (click)="relationModeChange.emit(!relationMode())"
    >
      <span class="iching-reading__switch-track" aria-hidden="true"><span class="iching-reading__switch-thumb"></span></span>
      {{ t('humanDesign.iching.reading.relationMode') }}
    </button>
    @if (relationMode() && resonanceTrigram(); as resonance) {
      <div class="iching-reading__card" data-test-id="iching-resonance">
        <span class="iching-reading__card-label">{{ t('humanDesign.iching.reading.resonance') }}: {{ resonance.name }}</span>
        <p>{{ resonance.asResonance }}</p>
      </div>
    }
  </section>

  <!-- C2: de bewegende lijnen -->
  <section class="iching-reading__step" data-step="moving">
    @if (step('moving'); as s) {
      <h4 class="iching-reading__step-title">{{ s.title }}</h4>
      <hd-iching-step-help [step]="s" />
    }
    <hd-iching-moving-lines [reading]="reading()" [method]="method()" [coinFaces]="coinFaces()" />
  </section>

  <!-- C3: de wortel -->
  <section class="iching-reading__step" data-step="nuclear">
    @if (step('nuclear'); as s) {
      <h4 class="iching-reading__step-title">{{ s.title }}</h4>
      <hd-iching-step-help [step]="s" />
    }
    <hd-iching-root [reading]="reading()" [method]="method()" [nuclearEntry]="nuclearEntry()" (openHexagram)="openHexagram.emit($event)" />
  </section>

  <!-- C4: de tekst -->
  <section class="iching-reading__step iching-reading__text" data-step="text">
    @if (step('text'); as s) {
      <h4 class="iching-reading__step-title">{{ s.title }}</h4>
      <hd-iching-step-help [step]="s" />
      @if (s.readingOrder?.length) {
        <div class="iching-reading__order">
          <span class="iching-reading__card-label">{{ t('humanDesign.iching.reading.readingOrder') }}</span>
          <ol>
            @for (item of s.readingOrder ?? []; track $index) {
              <li>{{ item }}</li>
            }
          </ol>
        </div>
      }
    }

    @if (termsInText().length > 0) {
      <div class="iching-reading__terms" data-test-id="iching-terms">
        <span class="iching-reading__card-label">{{ t('humanDesign.iching.reading.terms') }}</span>
        <dl>
          @for (term of termsInText(); track term.id) {
            <div class="iching-reading__term">
              <dt>{{ term.term }}</dt>
              <dd>{{ term.short }}</dd>
            </div>
          }
        </dl>
      </div>
    }

    @if (showAllLinesHint()) {
      <p class="iching-reading__all-lines" data-test-id="iching-all-lines-hint">{{ t('humanDesign.iching.reading.allLinesWilhelm') }}</p>
    }

    @if (hexagram(); as h) {
      <hd-iching-hexagram-reader
        [hexagram]="h"
        [hideRelations]="true"
        [openLines]="reading().movingPositions"
        [hideExplanations]="!showExplanations()"
        [markable]="true"
        [terms]="method().terms"
        [highlightWords]="keywords()"
        [commentary]="commentary()"
        [commentaryBusy]="commentaryBusy()"
        [trigramNames]="trigramNames()"
        [hideTitle]="!nameRevealed()"
        (titleRevealRequested)="nameRevealedChange.emit(true)"
        (wordClicked)="toggleKeyword($event)"
        (commentaryRequested)="commentaryRequested.emit()"
      />
      <button
        type="button"
        class="iching-reading__btn"
        data-test-id="iching-show-explanations"
        [attr.aria-expanded]="showExplanations()"
        (click)="showExplanations.set(!showExplanations())"
      >
        {{ showExplanations() ? t('humanDesign.iching.reading.hideExplanations') : t('humanDesign.iching.reading.showExplanations') }}
      </button>
      @if (!showExplanations()) {
        <p class="iching-reading__hint" data-test-id="iching-explanations-hint">{{ t('humanDesign.iching.reading.explanationsHint') }}</p>
      }
    } @else {
      <div class="iching-reading__loading" role="status"><e-splash-loader [size]="32" /></div>
    }
  </section>

  <!-- C5: het omslaghexagram -->
  @if (reading().changed !== undefined) {
    <section class="iching-reading__step" data-step="change">
      @if (step('change'); as s) {
        <h4 class="iching-reading__step-title">{{ s.title }}</h4>
        <hd-iching-step-help [step]="s" />
      }
      <hd-iching-change
        [reading]="reading()"
        [method]="method()"
        [hexagram]="nameRevealed() ? hexagram() : undefined"
        [changedHexagram]="changedHexagram()"
      />
    </section>
  }

  <!-- C6: de praktijk -->
  <section class="iching-reading__step" data-step="practice">
    @if (step('practice'); as s) {
      <h4 class="iching-reading__step-title">{{ s.title }}</h4>
      <hd-iching-step-help [step]="s" />
    }
    <hd-iching-practice
      [keywords]="keywords()"
      [notes]="notes()"
      [doList]="doList()"
      [dontList]="dontList()"
      [revisit]="step('practice')?.revisit"
      [saving]="saving()"
      [saved]="saved()"
      [canRevisit]="canRevisit()"
      (keywordsChange)="keywordsChange.emit($event)"
      (notesChange)="notesChange.emit($event)"
      (doListChange)="doListChange.emit($event)"
      (dontListChange)="dontListChange.emit($event)"
      (save)="save.emit()"
      (copy)="copy.emit()"
      (revisitRequested)="revisitRequested.emit()"
    />
  </section>
</div>
